/**
 * Lock Service
 * 
 * Business logic for transcript lock management
 */

import transcriptApi from '../../api/transcriptApi';
import { CONFIG } from '../../components/TranscriptEditor/constants/config';

/**
 * Lock Service Class
 */
class LockService {
  /**
   * Get lock status for a session
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Lock status response
   */
  static async getLockStatus(sessionId) {
    try {
      const response = await transcriptApi.getLockStatus(sessionId);
      return {
        success: true,
        lockInfo: response.lock_info || response,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to check lock status',
      };
    }
  }

  /**
   * Acquire lock for a session
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Lock acquisition response
   */
  static async acquireLock(sessionId) {
    try {
      const response = await transcriptApi.acquireLock(sessionId);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to acquire lock',
      };
    }
  }

  /**
   * Release lock for a session
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Lock release response
   */
  static async releaseLock(sessionId) {
    try {
      const response = await transcriptApi.releaseLock(sessionId);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to release lock',
      };
    }
  }

  /**
   * Force override lock (takeover from another user)
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Lock override response
   */
  static async forceOverrideLock(sessionId) {
    try {
      const response = await transcriptApi.forceOverrideLock(sessionId);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to force takeover lock',
      };
    }
  }

  /**
   * Get lock check interval
   * @returns {number} Lock check interval in milliseconds
   */
  static getLockCheckInterval() {
    return CONFIG.LOCK_CHECK_INTERVAL;
  }
}

export default LockService;

